import { useState } from "react";
import { CheckIcon, ChevronsUpDownIcon, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import type { EnumOption } from "@/lib/enum-options";

export interface EnumMultiSelectProps<T extends string> {
  /** The full set of selectable options. Build these with `enumOptions(values)` (no `all`). */
  options: EnumOption<T>[];
  /** Currently selected values. Empty array = nothing selected (caller should treat as "all"). */
  value: T[];
  onChange: (next: T[]) => void;
  placeholder?: string;
  searchPlaceholder?: string;
  disabled?: boolean;
  className?: string;
}

export function EnumMultiSelect<T extends string>({
  options,
  value,
  onChange,
  placeholder = "All",
  searchPlaceholder = "Search...",
  disabled = false,
  className,
}: EnumMultiSelectProps<T>) {
  const [open, setOpen] = useState(false);
  const selectedSet = new Set(value);

  // Preserve the canonical option order regardless of click order.
  function toggle(next: T) {
    if (selectedSet.has(next)) {
      onChange(value.filter((v) => v !== next));
    } else {
      const merged = [...value, next];
      merged.sort(
        (a, b) =>
          options.findIndex((o) => o.value === a) -
          options.findIndex((o) => o.value === b),
      );
      onChange(merged);
    }
  }

  const labelFor = (v: T) => options.find((o) => o.value === v)?.label ?? v;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className={cn("h-auto min-h-9 justify-between font-normal", className)}
        >
          <div className="flex flex-wrap gap-1 py-0.5">
            {value.length === 0 ? (
              <span className="text-muted-foreground">{placeholder}</span>
            ) : (
              value.map((v) => (
                <Badge
                  key={v}
                  variant="secondary"
                  className="gap-1 pr-1"
                  onClick={(e) => e.stopPropagation()}
                >
                  <span>{labelFor(v)}</span>
                  <button
                    type="button"
                    aria-label={`Remove ${labelFor(v)}`}
                    className="rounded-full p-0.5 hover:bg-muted-foreground/20"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      toggle(v);
                    }}
                  >
                    <X className="size-3" />
                  </button>
                </Badge>
              ))
            )}
          </div>
          <ChevronsUpDownIcon className="ml-2 size-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-(--radix-popover-trigger-width) p-0" align="start">
        <Command>
          <CommandInput placeholder={searchPlaceholder} />
          <CommandList>
            <CommandEmpty>No options found.</CommandEmpty>
            <CommandGroup>
              {options.map((opt) => {
                const isSelected = selectedSet.has(opt.value as T);
                return (
                  <CommandItem
                    key={opt.value}
                    value={opt.label}
                    onSelect={() => toggle(opt.value as T)}
                  >
                    <CheckIcon
                      className={cn("mr-2 size-4", isSelected ? "opacity-100" : "opacity-0")}
                    />
                    {opt.label}
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
